import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { merge } from 'lodash';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { DEFAULT_EXCLUDE_FALSY_TOOGLE, Metric, Result, MetricValue, DateRangeValue, BenchmarkService, DatesService, Query, DynamicDateRange, Row, MessageService, EMPTY_FALSY_ERROR, TrackingService, WidgetQueueProxyService } from '@hub-components';
import { MetricFormat } from 'app/apps/studio/app/models/dashboard.model';
import { BenchmarkSelection } from 'app/hub-components/components/benchmarks/benchmarks.component';
import { DEFAULT_METRIC_LABEL, DEFAULT_KPI_METRIC, DEFAULT_COMPARE_TO } from '../../../../constants';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';
import { ChartOptions } from '../chart-options';
import { DEFAULT_STYLE_OPTIONS } from '../default-chart-options';
import { resolveDashboardMetricFormat } from '../widget-metric-format.util';

type MetricComparison = 'green_increase' | 'red_increase';

const SUPPORTED_INPUTS = [
  {
    bindTo: 'metrics',
    label: 'Metrics',
    type: 'metrics',
  },
];

interface KpiChartOptions extends ChartOptions {
  icons: { [key: string]: string },
  baseColor: string,
  backgroundColor: string,
  metricLabel: boolean | { enabled: boolean, align: string, size: number },
  kpiMetric: { size: number, align: string },
  dateRange: { enabled: boolean, value: DateRangeValue },
}

@Component({
  selector: 'kpi-chart',
  templateUrl: 'kpi.component.html',
  styleUrls: ['./kpi.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class KpiComponent implements OnChanges {
  @HostBinding('style.backgroundColor') backgroundColor: string;

  @Input() result: Result;
  @Input() metrics: Metric[];
  @Input() style: WidgetStyle;
  @Input() chartOptions: KpiChartOptions;
  @Input() dashboardInterval: DynamicDateRange;
  @Input() dashboardMetricFormats: MetricFormat[] = [];

  static queryOverrides = {
    groupBy: [],
  };

  static supportedInputs = SUPPORTED_INPUTS;
  static supportedOptions = [
    {
      bindTo: 'dataOptions',
      label: 'Data',
      type: 'dataOptions',
      options: [
        {
          bindTo: 'excludeFalsy',
        },
      ],
    }, {
      bindTo: 'title',
      label: 'Title',
      type: 'textFormat',
      options: [
        {
          bindTo: 'text',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    }, {
      bindTo: 'kpiMetric',
      label: 'Metric',
      type: 'textFormat',
      options: [
        {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    }, {
      bindTo: 'metricLabel',
      label: 'Metric Label',
      type: 'textFormat',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'align',
        }, {
          bindTo: 'size',
        },
      ],
    }, {
      bindTo: 'footer',
      label: 'Footer',
      type: 'footerCheckbox',
      options: [
        {
          bindTo: 'datasetInfo',
        },
        {
          bindTo: 'dateRange',
        },
        {
          bindTo: 'comparisonDateRange',
        },
      ],
    }, {
      bindTo: 'dateRange',
      label: 'Date range',
      type: 'dateRange',
      options: [
        {
          bindTo: 'enabled',
        }, {
          bindTo: 'value',
        },
      ],
    }, {
      bindTo: 'compareTo',
      label: 'Compare To',
      type: 'compareToDropdown',
    }, {
      bindTo: 'compareToValue',
      type: 'compareToValue',
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = DEFAULT_STYLE_OPTIONS;
  static supportedRollups = ['total'];
  public kpis: MetricValue[] = [];
  private _resultData: Row[];
  private _filteredResultData: Row[];
  private resultRows: Row[];
  private _kpis: MetricValue[] = [];
  private _filteredKpis: MetricValue[] = [];
  private excludeFalsy = DEFAULT_EXCLUDE_FALSY_TOOGLE;
  private isFilterEmpty = false;
  metricColor: string;
  metricLabel: { enabled: boolean, align: string, size: number } = DEFAULT_METRIC_LABEL;
  textColor: string;
  kpiMetric: { size: number, align: string } = DEFAULT_KPI_METRIC;
  title: { [key: string]: string | number };
  footer: any;
  public footerData: any;
  public comparisonDateRange: string;

  icons: { [key: string]: string };

  datasetDisplayName: string;

  compareTo: BenchmarkSelection = DEFAULT_COMPARE_TO;
  benchmarkPages: any;
  benchmarkValues: { [metricName: string]: number } = {};
  showComparisonValue: boolean = false;
  loadingBenchmarks: boolean = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private benchmarkService: BenchmarkService,
    private datesService: DatesService,
    private messageService: MessageService,
    private trackingService: TrackingService,
    private widgetQueueProxy: WidgetQueueProxyService,
  ) { }

  ngOnChanges(changes) {
    if (this.result && this.metrics) {
      this.datasetDisplayName = <string> this.result.query.schema.displayName;
      const row = this.result.rows[0];
      if (row) {
        this._resultData = this.getResultsRowsData(this.result.rows, false);
        this._filteredResultData = this.getResultsRowsData(this.result.rows, true);
        this.isFilterEmpty = this._filteredResultData.length === 0 && this.result.rows.length !== 0;
        if (this.isFilterEmpty && this.excludeFalsy) {
          this.messageService.info(EMPTY_FALSY_ERROR);
        }
        const metrics = row.metrics;
        this._kpis = this.metrics.map(m => metrics.find(metric => metric.name === m.name));
        this._filteredKpis = this._kpis.filter(metric => !!metric.value);
        this.updateKPIdata();
        this.updateResultRowsData();
      }
    }

    if (this.style) {
      this.backgroundColor = this.style.backgroundColor;
      this.metricColor = this.style.baseColor;
      this.textColor = this.style.textColor;
    }
    if (this.chartOptions) {
      if (this.chartOptions.dataOptions) {
        if (!this.excludeFalsy && this.isFilterEmpty && this.chartOptions.dataOptions.excludeFalsy) {
          this.messageService.info(EMPTY_FALSY_ERROR);
        }
        if (this.excludeFalsy !== this.chartOptions.dataOptions.excludeFalsy) {
          this.addTracking('excludeFalsy', changes);
          this.excludeFalsy = this.chartOptions.dataOptions.excludeFalsy;
        }
        this.updateKPIdata();
        this.updateResultRowsData();
      }

      if (this.chartOptions.footer) {
        if (this.footer !== undefined) {
          if (this.footer.dateRange !== this.chartOptions.footer?.dateRange) {
            this.addTracking('footerDateRange', changes);
          }
          if (this.footer.datasetInfo !== this.chartOptions.footer?.datasetInfo) {
            this.addTracking('footerDatasetInfo', changes);
          }
          if (this.footer.comparisonDateRange !== this.chartOptions.footer?.comparisonDateRange) {
            this.addTracking('footerComparisonDateRange', changes);
          }
        }

        this.footer = this.chartOptions.footer;
        this.footerData = {
          datasetInfo: this.result?.query?.schema?.displayName,
          dateRange: this.datesService.intervalDateRange(this.dashboardInterval, this.result?.query?.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined),
        };
      }

      if (this.chartOptions.title) {
        this.title = this.chartOptions.title;
        this.title.align = this.chartOptions.title.align || 'center';
      }
      if (this.chartOptions.kpiMetric) {
        this.kpiMetric = this.chartOptions.kpiMetric;
      }
      if (this.chartOptions.metricLabel !== undefined) {
        if (typeof this.chartOptions.metricLabel === 'boolean') {
          this.metricLabel = { ...DEFAULT_METRIC_LABEL, enabled: this.chartOptions.metricLabel };
        } else {
          this.metricLabel = this.chartOptions.metricLabel;
        }
      }
      this.addTracking('compareToValue', changes);
      this.showComparisonValue = !!this.chartOptions.compareToValue &&
        !!this.chartOptions.compareTo?.value &&
        this.chartOptions.compareTo.value !== 'none';

      if (this.chartOptions.compareTo) {
        this.addTracking('compareTo', changes);
        this.compareTo = {
          name: this.chartOptions.compareTo.name,
          value: this.chartOptions.compareTo.value,
          dateRange: this.chartOptions.compareTo.dateRange,
          query: this.chartOptions.compareTo.query,
        };
        if (this.result && this.result.query) {
          const dateRange = this.chartOptions.dateRange && this.chartOptions.dateRange.enabled ? this.chartOptions.dateRange.value : this.dashboardInterval;
          const { startDate, endDate } = this.datesService.parseValue(dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
          const dateRangeInvalid = !this.datesService.isValid(startDate, endDate);
          const benchmarkDates = dateRangeInvalid ? undefined : this.getBenchmarkDates(dateRange);
          this.comparisonDateRange = benchmarkDates ? this.datesService.intervalDateRange(benchmarkDates) : undefined;

          if (!dateRangeInvalid) {
            if (benchmarkDates) {
              this.loadingBenchmarks = true;
              const benchmarkBase = this.result.query.clone();
              benchmarkBase.interval = this.intervalFromDates(benchmarkDates);
              benchmarkBase.auth = this.chartOptions.compareTo.query && this.chartOptions.compareTo.query['auth'];
              this.chartOptions.compareTo.query = merge(benchmarkBase.asJson(), { auth: benchmarkBase.auth }, { deep: true });
              const benchmarkResults = this.runQuery(benchmarkBase);
              this.updateBenchmarkPages(benchmarkResults);
            }
          }
        }
      }
      this.icons = this.chartOptions.icons;
    }
  }

  public getKpiFormat(kpi: MetricValue) {
    return resolveDashboardMetricFormat(kpi.name, kpi.format, this.result?.query?.dataset, this.dashboardMetricFormats);
  }

  private updateBenchmarkPages(benchmarkResults) {
    from(benchmarkResults).subscribe((b: Result) => {
      const benchRowsData = this.getResultsRowsData(b.rows, this.excludeFalsy);
      const benchmarksBySlices = benchRowsData.reduce((memo, row) => {
        const key = row.slices.map(s => s.id).join('-');
        const values = row.metrics.reduce((memo, metric) => {
          memo[metric.name] = metric.value;
          return memo;
        }, {});
        memo[key] = values;
        return memo;
      }, {});

      const benchmarks = this.resultRows.map(row => {
        const key = row.slices.map(s => s.id).join('-');
        return benchmarksBySlices[key];
      });

      if (benchmarks && benchmarks.length) {
        this.benchmarkPages = this.benchmarkService.createBenchmarkPages(this.resultRows, benchmarks, 1);
        this.benchmarkValues = benchmarks[0] || {};
        this.loadingBenchmarks = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private runQuery(query: Query) {
    return this.widgetQueueProxy
      .analiticsQueueProxy(query, { skipLoader:true, cache:true })
      .pipe(
        map(qr => Result.parse(qr)),
      );
  }

  private intervalFromDates(dates) {
    return this.datesService.fixedQueryInterval(this.datesService.parseValue(dates, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined));
  }

  private getBenchmarkDates(dates: DateRangeValue) {
    if (this.compareTo.value && this.compareTo.value !== 'none') {
      return this.benchmarkService.calculateBenchmark(dates, this.compareTo.value, this.compareTo.dateRange, this.result.query.firstDayOfWeek ? Number(this.result.query.firstDayOfWeek) : undefined);
    } else {
      return null;
    }
  }

  private updateKPIdata() {
    if (this.excludeFalsy) {
      this.kpis = this._filteredKpis;
    } else {
      this.kpis = this._kpis;
    }
  }

  private updateResultRowsData() {
    if (this.excludeFalsy) {
      this.resultRows = this._filteredResultData;
    } else {
      this.resultRows = this._resultData;
    }
  }

  private getResultsRowsData(resultRows, excludeFalsy): Row[] {
    return resultRows.filter((row: Row) => excludeFalsy ? row.metrics.some(metric => !!metric.value) : true);
  }

  getComparison(metric): MetricComparison {
    if (
      metric.metric &&
      metric.metric.meta &&
      metric.metric.meta.display &&
      metric.metric.meta.display.format &&
      metric.metric.meta.display.format.comparison
    ) {
      return metric.metric.meta.display.format.comparison;
    }
    return 'green_increase';
  }

  getBenchmarkClassNames(comparison: MetricComparison, value: number) {
    if (value === 0) {
      return '';
    }
    if ((value > 0 && comparison === 'green_increase') || (value < 0 && comparison === 'red_increase') ) {
      return 'benchmark__green';
    }
    return 'benchmark__red';
  }

  private addTracking(type: string, changes: SimpleChanges) {
    if (changes?.options) {
      const { currentValue, previousValue } = changes?.options;

      if (type === 'excludeFalsy' && previousValue?.dataOptions && currentValue?.dataOptions) {
        if (previousValue.dataOptions.excludeFalsy !== currentValue.dataOptions.excludeFalsy) {
          this.trackingService.track('KPI Widget: Exclude N/A and 0 values', { value: this.chartOptions.dataOptions.excludeFalsy });
        }
      }

      if (previousValue?.footer && currentValue?.footer) {
        if (type === 'footerDateRange' && previousValue?.footer?.dateRange !== currentValue?.footer?.dateRange) {
          this.trackingService.track('KPI Widget: Footer DateRange', { value: this.chartOptions.footer.dateRange });
        }
        if (type === 'footerDatasetInfo' && previousValue?.footer?.datasetInfo !== currentValue?.footer?.datasetInfo) {
          this.trackingService.track('KPI Widget: Footer Dataset Info', { value: this.chartOptions.footer.datasetInfo });
        }
        if (type === 'footerComparisonDateRange' && previousValue?.footer?.comparisonDateRange !== currentValue?.footer?.comparisonDateRange) {
          this.trackingService.track('KPI Widget: Footer Comparison DateRange', { value: this.chartOptions.footer.comparisonDateRange });
        }
      }

      if (type === 'compareTo' && previousValue?.compareTo && currentValue?.compareTo) {
        if (previousValue?.compareTo.value !== currentValue?.compareTo.value) {
          this.trackingService.track('KPI Widget: Compare To', { value: this.chartOptions.compareTo.name, dateRange: this.chartOptions.compareTo.dateRange });
        }
      }

      if (type === 'compareToValue' && previousValue?.compareToValue !== undefined && previousValue.compareToValue !== currentValue?.compareToValue) {
        this.trackingService.track('KPI Widget: Show comparison value', { value: this.chartOptions.compareToValue });
      }
    }
  }
}
